// ペンストロークの点列を、送信・保存に適した形へ正規化する純関数群。
//
// 背景: 確定 op（pen.stroke.commit）は蓄積した全点をそのまま送っており、点数にも
// 直列化サイズにも上限が無かった。API Gateway WebSocket の frame size 上限は 32KB、
// message payload 上限は 128KB で、いずれも引き上げ不可。UA がメッセージを複数フレームへ
// 自動分割するかは標準（RFC 6455 / WHATWG）で規定されていないため、小さい方（32KB）を
// 基準に収める。上限を超えると API Gateway が code 1009 で接続を閉じ、Lambda が起動しない
// ためサーバログにも何も残らない。そのときに送信中だった他の op も失われ、再接続後の
// 権威スナップショットで図形が元位置へ巻き戻る（2026-09-02 の本番事象）。
// 丸め無しの 1 点は約 60 バイトのため、約 550 点で 32KB に到達する（60Hz で連続約 9 秒）。
// UA が分割する場合でも約 2,200 点で 128KB に到達し、いずれにせよ上限が必要である。
//
// 見た目への影響: 描画は perfect-freehand の getStroke を streamline 0.5 / smoothing 0.5 で
// 通しており、入力点は既に強く平滑化されている。丸め（画面 0.25px 相当）と
// 近接点除去（画面 0.5px 相当）は、平滑化後の輪郭には現れない。
// いずれもステージ定数ではなく zoom で割ってステージ値へ換算する。

import type { StrokePoint } from "./pen-stroke";

/** 座標の量子化目標（画面 px）。ステージ座標の刻みは これ / zoom となる。 */
const PEN_QUANTIZE_SCREEN_PX = 0.25;
/** 座標の丸め桁数の下限と上限。上限を固定して 1 点のバイト数に上界を与える。 */
const COORD_DECIMALS_MIN = 1;
const COORD_DECIMALS_MAX = 2;
/** 筆圧の丸め桁数（小数第 2 位）。thinning 0.5 の線幅差として視認できない粒度。 */
const PRESSURE_DECIMALS = 2;
/**
 * これ未満しか離れていない点は落とす（画面 px）。ステージ座標の閾値は これ / zoom。
 * ステージ座標の固定値にすると、最大ズーム 16 倍で画面 8px 相当となり、
 * 拡大して描いた細かい曲線・角・折り返しをまとめて落としてしまう。
 */
export const PEN_MIN_POINT_DISTANCE_SCREEN_PX = 0.5;
/**
 * pen.stroke.commit の op.apply メッセージ全体に許すバイト数。
 * 送信メッセージ全体の上限 30,720 バイト（websocket-realtime の CLIENT_MESSAGE_MAX_BYTES）
 * から 6KB を残す。この余裕の役割は 2 つある。
 * 1. pen.stroke.commit が送信サイズガード（最終防衛）に掛からないようにする。掛かると
 *    「どんな長さのストロークでも保存・共有できる」という本対応の目的を満たせない。
 * 2. サーバが op.commit として再配信する際に増える分（type 名と serverSeq。op 本体は
 *    加工せず echo する）と、将来 op.commit の形にフィールドが増えた場合の緩衝。
 */
export const PEN_STROKE_MESSAGE_MAX_BYTES = 24 * 1024;
/** 点配列の予算の下限。始端・終端の 2 点は約 90 バイトのため必ず収まる。 */
export const PEN_STROKE_MIN_POINTS_BUDGET_BYTES = 512;
/**
 * 速い経路のための件数上限。まず件数で粗く落としてからバイト数で追い込む。
 * 上限の保証はバイト数側が担う（本定数は保証しない）。
 */
export const PEN_STROKE_MAX_POINTS = 700;
/**
 * 一時 op（pen.stroke.append）1 通あたりの点数上限。
 * サーバスキーマ（canvas-op-schema の points: z.array(PointSchema).min(1).max(512)）に
 * 合わせる。超えるとスキーマ検証で拒否され、プレビューが他参加者へ無音で届かなくなる。
 */
export const PEN_TRANSIENT_MAX_POINTS = 512;
/**
 * 確定 op の「正規化 → 枠計算 → 最終メッセージ実測」の反復上限。
 * 封筒サイズを最悪値で測っているため通常は 1 回で収束する。
 */
export const PEN_STROKE_FIT_MAX_ATTEMPTS = 3;
/** バイト数で収束しない場合に最後に残す点数（始端と終端）。 */
const PEN_STROKE_ABSOLUTE_MIN_POINTS = 2;
/** バイト数駆動の間引きの反復上限。比例縮小のため通常 1〜2 回で収束する。 */
const BYTE_FIT_MAX_ITERATIONS = 5;

/**
 * 入力中の全体再描画に使う表示専用点列を、固定点を失わず上限内へ間引く。
 * 固定 index（始点・Shift anchor・終点など）を先に予約し、残枠を各固定点間の
 * 元点数比で配分する。入力配列と筆圧値は変更しない。
 */
export function samplePenPreviewPoints(
  points: readonly StrokePoint[],
  fixedIndices: readonly number[],
  maxPoints = PEN_STROKE_MAX_POINTS
): readonly StrokePoint[] {
  if (points.length <= maxPoints) return points;
  const limit = Math.max(1, Math.floor(maxPoints));
  const fixed = [...new Set([0, points.length - 1, ...fixedIndices])]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < points.length)
    .sort((a, b) => a - b)
    .slice(0, limit);
  if (fixed.length >= limit) return fixed.map((index) => points[index]!).filter(Boolean);

  const segments = fixed.slice(0, -1).map((start, index) => {
    const end = fixed[index + 1]!;
    return { start, end, candidates: Math.max(0, end - start - 1), allocation: 0, remainder: 0 };
  });
  // 各区間の候補点数を合計し、残枠を元の点密度に比例して配分する。
  const candidateTotal = segments.reduce((sum, segment) => sum + segment.candidates, 0);
  let remaining = Math.min(limit - fixed.length, candidateTotal);
  if (candidateTotal > 0) {
    for (const segment of segments) {
      const exact = (remaining * segment.candidates) / candidateTotal;
      segment.allocation = Math.floor(exact);
      segment.remainder = exact - segment.allocation;
    }
    // 切り捨て後の配分済み件数を合計し、余りを端数の大きい区間から 1 点ずつ補う。
    let unassigned = remaining - segments.reduce((sum, segment) => sum + segment.allocation, 0);
    for (const segment of [...segments].sort((a, b) => b.remainder - a.remainder)) {
      if (unassigned <= 0) break;
      if (segment.allocation < segment.candidates) {
        segment.allocation += 1;
        unassigned -= 1;
      }
    }
  }

  const selected = new Set(fixed);
  for (const segment of segments) {
    for (let index = 1; index <= segment.allocation; index += 1) {
      selected.add(
        Math.round(segment.start + ((segment.end - segment.start) * index) / (segment.allocation + 1))
      );
    }
  }
  // 丸めで重複した場合だけ、元順を保った未選択点で残枠を補う。
  remaining = limit - selected.size;
  for (let index = 0; index < points.length && remaining > 0; index += 1) {
    if (!selected.has(index)) {
      selected.add(index);
      remaining -= 1;
    }
  }
  return [...selected]
    .sort((a, b) => a - b)
    .map((index) => points[index]!)
    .filter(Boolean);
}

/**
 * 直列化済み文字列の UTF-8 バイト数。
 * String.length は UTF-16 コード単位数であり、日本語を含む本文では実バイト数と大きくずれる。
 * 送信サイズガード（websocket-realtime）と同じ方式で測り、判定基準をそろえる。
 */
export function utf8ByteLength(payload: string): number {
  return new TextEncoder().encode(payload).length;
}

function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  const scaled = value * factor;
  // 入力が有限でも、乗算で Infinity へオーバーフローし得る
  // （例 Number.MAX_VALUE * 100）。その場合 Math.round は Infinity を返し、
  // JSON.stringify は null を出して受信側のスキーマ検証を落とす。
  // 一時 op（pen.stroke.append）は clampPointToCanvas を通らないため、
  // ここで弾かないと不正な payload がそのまま送られる。
  if (!Number.isFinite(scaled)) return 0;
  return Math.round(scaled) / factor;
}

/**
 * 画面 PEN_QUANTIZE_SCREEN_PX 以下の刻みになる最小の小数桁数（1..2）。
 * zoom が 0 以下・非有限の異常値なら下限桁数を返す。
 */
function coordDecimalsForZoom(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) return COORD_DECIMALS_MIN;
  const stageStep = PEN_QUANTIZE_SCREEN_PX / zoom;
  const decimals = Math.ceil(-Math.log10(stageStep));
  return Math.min(COORD_DECIMALS_MAX, Math.max(COORD_DECIMALS_MIN, decimals));
}

/** 画面 PEN_MIN_POINT_DISTANCE_SCREEN_PX 相当のステージ座標距離。 */
function minPointDistanceForZoom(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) return PEN_MIN_POINT_DISTANCE_SCREEN_PX;
  return PEN_MIN_POINT_DISTANCE_SCREEN_PX / zoom;
}

/**
 * 座標を zoom に応じた桁数へ、筆圧を固定桁数へ丸めた点列を返す。
 * キーの並びは従来の送信形（x → y → pressure）を維持し、直列化結果の形を変えない。
 */
function roundPoints(points: readonly StrokePoint[], decimals: number): StrokePoint[] {
  return points.map((point) => ({
    x: roundTo(point.x, decimals),
    y: roundTo(point.y, decimals),
    pressure: roundTo(point.pressure, PRESSURE_DECIMALS),
  }));
}

/**
 * 点列を丸め、近接点を落とし、直列化後のバイト数が予算へ収まるまで等間隔に間引く。
 * 始端と終端は必ず保持する（ストロークの開始・終了位置が動かないようにする）。
 *
 * 点数を固定値で打ち切る方式は採らない。既存仕様は「キャンバス外にも配置可」であり
 * clampPointToCanvas も座標範囲を制限しないため、座標の桁数に上限が無く、
 * 固定点数ではバイト数の上限を保証できない。
 */
export function normalizePenStrokePoints(
  points: readonly StrokePoint[],
  zoom: number,
  pointsBudgetBytes: number
): readonly StrokePoint[] {
  if (points.length === 0) return [];
  const budget = Math.max(PEN_STROKE_MIN_POINTS_BUDGET_BYTES, pointsBudgetBytes);
  const rounded = roundPoints(points, coordDecimalsForZoom(zoom));
  let current = dropNearPoints(rounded, minPointDistanceForZoom(zoom));
  if (current.length > PEN_STROKE_MAX_POINTS) {
    current = sampleEvenly(current, PEN_STROKE_MAX_POINTS);
  }
  for (let attempt = 0; attempt < BYTE_FIT_MAX_ITERATIONS; attempt += 1) {
    const bytes = utf8ByteLength(JSON.stringify(current));
    if (bytes <= budget) return current;
    if (current.length <= PEN_STROKE_ABSOLUTE_MIN_POINTS) return current;
    // 予算比で比例縮小し、丸め誤差ぶんの余裕（0.95）を掛ける。
    const proportional = Math.floor(((current.length * budget) / bytes) * 0.95);
    // 比例縮小が 1 件も減らせない場合（予算にほぼ等しいとき）は 1 件ずつ確実に減らす。
    const target = Math.max(
      PEN_STROKE_ABSOLUTE_MIN_POINTS,
      Math.min(proportional, current.length - 1)
    );
    current = sampleEvenly(current, target);
  }
  // 最終保証: 比例縮小で収まらなかった場合は、収まるまで半減させる。
  // 半減は log2(700) ≒ 10 回で 2 点（始端と終端）へ到達するため、このループは必ず終了する。
  // 予算の下限は 512 バイトで、2 点は座標が最大桁でも約 100 バイトに収まるため、
  // 実際の入力に対しては戻り値が予算を超えることはない
  // （数値表記が 200 バイトを超えるような座標は JavaScript の数値表現上あり得ない）。
  while (
    current.length > PEN_STROKE_ABSOLUTE_MIN_POINTS &&
    utf8ByteLength(JSON.stringify(current)) > budget
  ) {
    current = sampleEvenly(
      current,
      Math.max(PEN_STROKE_ABSOLUTE_MIN_POINTS, Math.floor(current.length / 2))
    );
  }
  return current;
}

/** 直前に採用した点から minDistance（ステージ px）未満の点を落とす（始端・終端は残す）。 */
function dropNearPoints(
  points: readonly StrokePoint[],
  minDistance: number
): readonly StrokePoint[] {
  if (points.length <= 2) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return points;
  const result: StrokePoint[] = [first];
  let anchor = first;
  for (let i = 1; i < points.length - 1; i += 1) {
    const point = points[i];
    if (!point) continue;
    if (Math.hypot(point.x - anchor.x, point.y - anchor.y) < minDistance) continue;
    result.push(point);
    anchor = point;
  }
  result.push(last);
  return result;
}

/** 先頭と末尾を固定して等間隔にサンプリングし、ちょうど max 点にする。 */
function sampleEvenly(points: readonly StrokePoint[], max: number): readonly StrokePoint[] {
  if (max >= points.length) return points;
  if (max <= 1) {
    const first = points[0];
    return first ? [first] : [];
  }
  const result: StrokePoint[] = [];
  const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i += 1) {
    const point = points[Math.round(i * step)];
    if (point) result.push(point);
  }
  return result;
}

/**
 * 一時 op（描画中プレビュー）の点列を丸め、サーバスキーマの件数上限と送信バイト数の
 * 両方へ収める。
 *
 * pen.stroke.append のサーバスキーマは points の件数上限（512）だけで、
 * 座標・筆圧の桁数に上限が無い。したがって「512 点なら最大 20KB」とは言えない
 * （キャンバス外の大きな桁の座標なら 1 点 60 バイト超になり得る）。
 * 確定経路と同じくバイト数でも保証する。
 */
export function roundPenStrokePoints(
  points: readonly StrokePoint[],
  zoom: number,
  budgetBytes: number
): readonly StrokePoint[] {
  const rounded = roundPoints(points, coordDecimalsForZoom(zoom));
  // 件数上限を先に適用する。バイト数が予算内でも、512 点を超えるとサーバスキーマで拒否される。
  const limited =
    rounded.length > PEN_TRANSIENT_MAX_POINTS
      ? sampleEvenly(rounded, PEN_TRANSIENT_MAX_POINTS)
      : rounded;
  const budget = Math.max(PEN_STROKE_MIN_POINTS_BUDGET_BYTES, budgetBytes);
  if (utf8ByteLength(JSON.stringify(limited)) <= budget) return limited;
  // 超過時は確定経路と同じ間引きへ委ねる（始端・終端は保持される）。
  // 一時 op は次の送信で置き換わるため、点密度が落ちてもプレビューの
  // 見た目への影響は一瞬である。
  return normalizePenStrokePoints(limited, zoom, budget);
}
